import { GridFilterModel, GridSortItem } from '@mui/x-data-grid-pro'
import { OptionsState } from 'components/Shared/Inputs/CheckBoxMultiGroup'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { getSortQueryText } from 'components/utils/filterAndSort'
import { createSelector } from 'reselect'

import {
    GetCampaignOverviewResponse,
    GetCampaignPerformanceResponse,
    GetTaskPerformanceResponse,
    GetIndividualPerformanceResponse,
    GetIndividualPerformanceStatsResponse,
    GetTopPerformersResponse,
    GetIndividualPerformanceRequest,
    FilterOption,
} from 'services/api/campaignReport'

export interface IndividualPerformanceData {
    results: {
        uuid: string
        tasks: {
            title: string
            performance: Array<{ [key: string]: number }>
        }[]
    }[]
    favorites: { [key: string]: boolean }
}

export interface TopPerformersData {
    results: {
        uuid: string
        name: string
    }[]
    favorites: { [key: string]: boolean }
}
export interface CampaignReportState {
    campaignOverview: GetCampaignOverviewResponse
    campaignPerformance: GetCampaignPerformanceResponse
    taskPerformance: GetTaskPerformanceResponse
    individualPerformance: GetIndividualPerformanceResponse
    individualPerformanceStats: GetIndividualPerformanceStatsResponse
    topPerformers: GetTopPerformersResponse
    individualPerformanceFilterModel: GridFilterModel | null
    individualPerformanceFilterOptions: OptionsState[]
    individualPerformanceSortItem: GridSortItem | null
    filters: FilterOption[]
    individualPerformanceSearchTerm: string
}

export const initialState: CampaignReportState = {
    campaignOverview: {
        total_completed: 0,
        total_interested: 0,
        total_follower_count: {},
        total_dropout_reject: 0,
    },
    campaignPerformance: {
        follower_total: null,
        tasks: [
            {
                uuid: '',
                title: '',
                follower: null,
                performance: {
                    reach: null,
                    view: null,
                    engagement: null,
                    engagement_rate: null,
                    content_engagement_rate: null,
                },
            },
        ],
        performance_total: {
            reach: null,
            view: null,
            engagement: null,
            engagement_rate: null,
            content_engagement_rate: null,
        },
    },
    taskPerformance: {
        tasks: [],
    },
    individualPerformance: {
        count: 0,
        next: null,
        previous: null,
        results: [],
    },
    individualPerformanceStats: {
        profile_follower: {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0,
            6: 0,
            7: 0,
            99: 0,
        },
        tasks: [],
    },
    topPerformers: {
        tasks: [],
    },
    individualPerformanceFilterModel: null,
    individualPerformanceFilterOptions: [],
    individualPerformanceSortItem: null,
    filters: [],
    individualPerformanceSearchTerm: '',
}

export enum CampaignReportActions {
    GET_CAMPAIGN_OVERVIEW = 'campaignReports/GET_CAMPAIGN_OVERVIEW',
    GET_CAMPAIGN_PERFORMANCE = 'campaignReports/GET_CAMPAIGN_PERFORMANCE',
    GET_CAMPAIGN_PERFORMANCE_SUCCESS = 'campaignReports/GET_CAMPAIGN_PERFORMANCE_SUCCESS',
    UPDATE_AMBASSADOR_FAVORITE = 'campaignReports/UPDATE_AMBASSADOR_FAVORITE',
    UPDATE_AMBASSADOR_FAVORITE_SUCCESS = 'campaignReports/UPDATE_AMBASSADOR_FAVORITE_SUCCESS',
    GET_TOP_PERFORMERS = 'campaignReports/GET_TOP_PERFORMERS',
    GET_TOP_PERFORMERS_SUCCESS = 'campaignReports/GET_TOP_PERFORMERS_SUCCESS',
    GET_TASK_PERFORMANCE = 'campaignReports/GET_TASK_PERFORMANCE',
    GET_TASK_PERFORMANCE_SUCCESS = 'campaignReports/GET_TASK_PERFORMANCE_SUCCESS',
    GET_CAMPAIGN_OVERVIEW_SUCCESS = 'campaignReports/GET_CAMPAIGN_OVERVIEW_SUCCESS',
    GET_INDIVIDUAL_PERFORMANCE = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE',
    GET_INDIVIDUAL_PERFORMANCE_SUCCESS = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE_SUCCESS',
    GET_INDIVIDUAL_PERFORMANCE_STATS = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE_STATS',
    GET_INDIVIDUAL_PERFORMANCE_STATS_SUCCESS = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE_STATS_SUCCESS',
    GET_INDIVIDUAL_PERFORMANCE_FILTERS = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE_FILTERS',
    GET_INDIVIDUAL_PERFORMANCE_FILTERS_SUCCESS = 'campaignReports/GET_INDIVIDUAL_PERFORMANCE_FILTERS_SUCCESS',
    SET_INDIVIDUAL_PERFORMANCE_SORT_ITEM = 'campaignReports/SET_INDIVIDUAL_PERFORMANCE_SORT_ITEM',
    SET_INDIVIDUAL_PERFORMANCE_FILTER_MODEL = 'campaignReports/SET_INDIVIDUAL_PERFORMANCE_FILTER_MODEL',
    SET_INDIVIDUAL_PERFORMANCE_FILTER_OPTIONS = 'campaignReports/SET_INDIVIDUAL_PERFORMANCE_FILTER_OPTIONS',
    SET_INDIVIDUAL_PERFORMANCE_SEARCH_TERM = 'campaignReports/SET_INDIVIDUAL_PERFORMANCE_SEARCH_TERM',
    DOWNLOAD_CAMPAIGN_REPORT = 'campaignReports/DOWNLOAD_CAMPAIGN_REPORT',
}

export const getCampaignOverview = (payload: { uuid: string }) => ({
    type: CampaignReportActions.GET_CAMPAIGN_OVERVIEW,
    payload,
})

export const getCampaignOverviewSuccess = (payload: GetCampaignOverviewResponse) => ({
    type: CampaignReportActions.GET_CAMPAIGN_OVERVIEW_SUCCESS,
    payload,
})

export const getCampaignPerformance = (payload: { uuid: string }) => ({
    type: CampaignReportActions.GET_CAMPAIGN_PERFORMANCE,
    payload,
})

export const getCampaignPerformanceSuccess = (payload: GetCampaignPerformanceResponse) => ({
    type: CampaignReportActions.GET_CAMPAIGN_PERFORMANCE_SUCCESS,
    payload,
})

export const getTaskPerformance = (payload: { uuid: string }) => ({
    type: CampaignReportActions.GET_TASK_PERFORMANCE,
    payload,
})

export const getTaskPerformanceSuccess = (payload: GetTaskPerformanceResponse) => ({
    type: CampaignReportActions.GET_TASK_PERFORMANCE_SUCCESS,
    payload,
})

export const getIndividualPerformance = (payload: GetIndividualPerformanceRequest) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE,
    payload,
})

export const getIndividualPerformanceSuccess = (payload: GetIndividualPerformanceResponse) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_SUCCESS,
    payload,
})

export interface UpdateAmbassadorFavoritePayload {
    partiposter_uuid: string
    favorited: boolean
    brand_uuid: string
    brand_name: string
}

export const UpdateAmbassadorFavorite = (payload: UpdateAmbassadorFavoritePayload) => ({
    type: CampaignReportActions.UPDATE_AMBASSADOR_FAVORITE,
    payload,
})

export const UpdateAmbassadorFavoriteSuccess = (payload: UpdateAmbassadorFavoritePayload) => ({
    type: CampaignReportActions.UPDATE_AMBASSADOR_FAVORITE_SUCCESS,
    payload,
})

export const getIndividualPerformanceStats = (payload: { uuid: string }) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_STATS,
    payload,
})

export const getIndividualPerformanceStatsSuccess = (payload: GetIndividualPerformanceStatsResponse) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_STATS_SUCCESS,
    payload,
})

export type TopPerformersTab =
    | 'most_engagement'
    | 'highest_engagement_rate'
    | 'highest_content_engagement_rate'
    | 'most_views'

export interface TopPerformersPayload {
    uuid: string
    tab: TopPerformersTab
    top: number
}

export const getTopPerformers = (payload: TopPerformersPayload) => ({
    type: CampaignReportActions.GET_TOP_PERFORMERS,
    payload,
})

export const getTopPerformersSuccess = (payload: GetTopPerformersResponse) => ({
    type: CampaignReportActions.GET_TOP_PERFORMERS_SUCCESS,
    payload,
})

interface SetFilterModelPayload {
    options: GridFilterModel | null
}

interface SetSortItemPayload {
    options:
        | (GridSortItem & {
              fieldLabel: string
              sortDirectionLabel: string
          })
        | null
}

export interface GetIndividualPerformanceFiltersPayload {
    uuid: string
}

export const getIndividualPerformanceFilters = (payload: GetIndividualPerformanceFiltersPayload) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_FILTERS,
    payload,
})

export const getIndividualPerformanceFiltersSuccess = (data: FilterOption[]) => ({
    type: CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_FILTERS_SUCCESS,
    payload: data,
})

export const setIndividualPerformanceFilterModel = (payload: SetFilterModelPayload) => ({
    payload,
    type: CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_FILTER_MODEL,
})

export const setIndividualPerformanceFilterOptions = (payload: OptionsState[]) => ({
    payload,
    type: CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_FILTER_OPTIONS,
})

export const setIndividualPerformanceSortItem = (payload: SetSortItemPayload) => ({
    payload,
    type: CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_SORT_ITEM,
})

export const setIndividualPerformanceSearchTerm = (payload: string) => ({
    payload,
    type: CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_SEARCH_TERM,
})

export const downloadCampaignReport = (payload: { uuid: string }) => ({
    type: CampaignReportActions.DOWNLOAD_CAMPAIGN_REPORT,
    payload,
})

export const campaignReportReducer = (state = initialState, action: any): CampaignReportState => {
    switch (action.type) {
        case CampaignReportActions.GET_CAMPAIGN_OVERVIEW_SUCCESS:
            return {
                ...state,
                campaignOverview: action.payload,
            }

        case CampaignReportActions.GET_CAMPAIGN_PERFORMANCE_SUCCESS:
            return {
                ...state,
                campaignPerformance: action.payload,
            }
        case CampaignReportActions.GET_TASK_PERFORMANCE_SUCCESS:
            return {
                ...state,
                taskPerformance: action.payload,
            }
        case CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_SUCCESS:
            return {
                ...state,
                individualPerformance: action.payload,
            }
        case CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_STATS_SUCCESS:
            return {
                ...state,
                individualPerformanceStats: action.payload,
            }

        case CampaignReportActions.UPDATE_AMBASSADOR_FAVORITE_SUCCESS:
            return {
                ...state,
                individualPerformance: {
                    ...state.individualPerformance,
                    results: state.individualPerformance.results.map(result => ({
                        ...result,
                        favorite:
                            result.partiposter_profile.uuid === action.payload.partiposter_uuid
                                ? action.payload.favorited
                                : result.favorite,
                    })),
                },
                topPerformers: {
                    ...state.topPerformers,
                    tasks: state.topPerformers.tasks.map(task => ({
                        ...task,
                        ranking: task.ranking.map(ranking => ({
                            ...ranking,
                            favorite:
                                ranking.partiposter_profile.uuid === action.payload.partiposter_uuid
                                    ? action.payload.favorited
                                    : ranking.favorite,
                        })),
                    })),
                },
            }

        case CampaignReportActions.GET_TOP_PERFORMERS_SUCCESS:
            return {
                ...state,
                topPerformers: action.payload,
            }
        case CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_FILTER_MODEL: {
            const { options } = action.payload
            return {
                ...state,
                individualPerformanceFilterModel: options,
            }
        }
        case CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_FILTER_OPTIONS: {
            return {
                ...state,
                individualPerformanceFilterOptions: action.payload,
            }
        }
        case CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_SORT_ITEM: {
            const { options } = action.payload
            return {
                ...state,
                individualPerformanceSortItem: options,
            }
        }
        case CampaignReportActions.GET_INDIVIDUAL_PERFORMANCE_FILTERS_SUCCESS: {
            return {
                ...state,
                filters: action.payload,
            }
        }
        case CampaignReportActions.SET_INDIVIDUAL_PERFORMANCE_SEARCH_TERM: {
            return {
                ...state,
                individualPerformanceSearchTerm: action.payload,
            }
        }
        default:
            return state
    }
}

export const selectCampaignOverview = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.campaignOverview
export const selectCampaignPerformance = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.campaignPerformance
export const selectTaskPerformance = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.taskPerformance
export const selectIndividualPerformance = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformance
export const selectIndividualPerformanceStats = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformanceStats
export const selectTopPerformers = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.topPerformers
export const selectIndividualPerformanceFilters = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.filters
export const selectIndividualPerformanceFilterOptions = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformanceFilterOptions
export const selectIndividualPerformanceFilterModel = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformanceFilterModel
export const selectIndividualPerformanceSortItem = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformanceSortItem

export const selectSelectedIndividualPerformanceSortItem = createSelector(
    [selectIndividualPerformanceSortItem],
    (gridSortItem): string | null => {
        if (gridSortItem && gridSortItem.field) {
            return getSortQueryText(gridSortItem.field, gridSortItem.sort || 'desc')
        } else {
            return null
        }
    },
)

export const selectIndividualPerformanceFilteringParams = createSelector(
    [selectIndividualPerformanceFilterModel],
    filterModel => {
        if (!filterModel) return {}

        const SUFFIX_BY_OPERATOR_MAP = {
            contains: 'contains',
            startsWith: 'startswith',
            endsWith: 'endswith',
            '=': 'eq',
            '!=': 'neq',
            '>': 'gt',
            '>=': 'gte',
            '<': 'lt',
            '<=': 'lte',
        }

        const { items } = filterModel as GridFilterModel
        if (!items) {
            return {}
        }
        return items.reduce((acc: { [key: string]: string | string[] | undefined }, curr) => {
            const columnField = curr.columnField
            if (!columnField || curr.value === undefined || curr.value === null) return acc

            const suffix = SUFFIX_BY_OPERATOR_MAP[curr.operatorValue as keyof typeof SUFFIX_BY_OPERATOR_MAP]

            if (columnField === FIELDS_TYPE.GENDER || columnField === FIELDS_TYPE.STATUS) {
                return {
                    ...acc,
                    [columnField]: acc[columnField] ? `${acc[columnField]},${curr.value}` : curr.value,
                }
            } else if (
                columnField === FIELDS_TYPE.IG_FOLLOWERS ||
                columnField === FIELDS_TYPE.FB_FRIENDS ||
                columnField === FIELDS_TYPE.TIKTOK_FOLLOWERS ||
                columnField === FIELDS_TYPE.TWITTER_FOLLOWERS
            ) {
                return {
                    ...acc,
                    social_media_follower_number_tier: acc.social_media_follower_number_tier
                        ? `${acc.social_media_follower_number_tier},${curr.value}`
                        : curr.value,
                }
            } else if (columnField === FIELDS_TYPE.NAME) {
                if (!suffix) return { ...acc, name: curr.value }
                return {
                    ...acc,
                    [`${FIELDS_TYPE.NAME}__${suffix}`]: curr.value,
                }
            } else if (columnField === FIELDS_TYPE.FAVORITE) {
                return {
                    ...acc,
                    favorite: acc.favorite ? `${acc.favorite},${curr.value}` : curr.value,
                }
            }
            return acc
        }, {})
    },
)
export const selectIndividualPerformanceSearchTerm = (state: { campaignReport: CampaignReportState }) =>
    state.campaignReport.individualPerformanceSearchTerm
